"use client";

import * as React from "react";
import { Slottable } from "@radix-ui/react-slot";
import { PlusIcon } from "lucide-react";

import { Button, type ButtonProps } from "./button";

/**
 * Props for {@link CreateButton}. The look is fixed — there is no `variant` or `size` — so every
 * create affordance reads the same wherever it appears.
 */
type CreateButtonProps = Omit<ButtonProps, "variant" | "size">;

/**
 * The leaf "+ Label" button, for create affordances a described action row cannot express: a
 * popover's adder, the `+ Add tag` trigger in a table cell, an empty state's call to action.
 *
 * Grey outline, 32px, with a 14px `Plus` built in. The caller chooses the verb ("Create" or "Add")
 * and writes the label in sentence case. With `asChild` the `Plus` is rendered inside the child,
 * so a link keeps the same look as the button.
 */
const CreateButton = React.forwardRef<HTMLButtonElement, CreateButtonProps>(
	({ asChild = false, children, ...props }, ref) => {
		const icon = (
			<PlusIcon key="icon" className="size-3.5" data-testid="create-icon" />
		);

		return (
			<Button
				ref={ref}
				variant="outline"
				size="sm"
				asChild={asChild}
				{...props}
			>
				{asChild ? (
					// `Slot` merges into its child, so the icon goes in through `Slottable` rather than
					// beside it.
					[icon, <Slottable key="child">{children}</Slottable>]
				) : (
					<>
						{icon}
						{children}
					</>
				)}
			</Button>
		);
	},
);
CreateButton.displayName = "CreateButton";

export { CreateButton };
export type { CreateButtonProps };
